/**
 * Fleet "what needs attention" engine — VESSELS-ONLY SLICE (FT-441 truth fix).
 *
 * A SINGLE, reusable derivation of the attention flags surfaced on the Fleet
 * landing VESSEL cards. It is deliberately framework-free (pure functions + a
 * thin, read-only server read) so the SAME engine can later feed the
 * notification bell (P-03) without a rewrite.
 *
 * ── FT-441: the truth rules (single source of truth) ─────────────────────────
 *   • Vessel overdue maintenance reproduces the DASHBOARD's own normalized rule
 *     EXACTLY — `deriveNormalizedMaintenanceStatus` mirrors
 *     `useNormalizedMaintenanceJobs` (summarizeJob → maintenance_history →
 *     lastCompleted+interval → initialNextDue → due-today), counted as overdue
 *     iff daysUntilDue < 0, excluding archived jobs. So the card count can never
 *     be a partial slice of the dashboard's count.
 *   • Vessel cert/doc expiry uses the compliance-register rule
 *     (useComplianceRegister.calculateStatus: expired < 0d, expiring <= 30d)
 *     across certificate AND sms_document categories — so an expired policy /
 *     SMS document raises a flag too.
 *
 * ── Data source: ONE batched SERVER pull (read-only) ─────────────────────────
 * A never-opened-this-session vessel is resident in neither localStorage nor
 * IndexedDB (the sync bridge only hydrates the ACTIVE vessel), so a cold card
 * cannot derive attention client-side. The data provision is therefore
 * SERVER-SIDE: `fetchFleetAttention()` makes ONE call to
 * GET /api/fleet/attention-data, which returns the raw stored rows for every
 * ASSIGNED vessel. Those raw rows are unwrapped with the SAME canonical
 * `extractItems` the sync bridge uses (FT-331 ghost-doc guard = the sole unwrap
 * authority), then fed through the UNCHANGED pure derive functions above. The
 * overdue / expiry rules never leave the FE, so the card count is guaranteed
 * identical to the in-vessel dashboard count.
 *
 * ── "Never a false all-clear" guard (FT-441 — safety property) ───────────────
 * If the batched call FAILS (network / auth / non-2xx — INCLUDING a 404 while
 * the endpoint is not yet deployed) EVERY vessel resolves to `unknown`
 * ("couldn't check") → the card shows "Checking…", never a false "all up to
 * date". If the call SUCCEEDS but a given assigned vessel is ABSENT from the
 * response, that vessel is also `unknown` (absent ≠ empty). A vessel PRESENT in
 * a successful response with empty collections is a legitimate all-clear.
 *
 * This is strictly READ-ONLY: the endpoint is a plain GET; nothing here writes
 * storage, enqueues a sync push / outbox item, or touches UniversalSyncBridge.
 * Rows are used only to compute counts and then discarded.
 *
 * ── Permission safety (FT-435 — do NOT regress) ──────────────────────────────
 * The endpoint derives the vessel set server-side from the JWT (per-user
 * assignment-gated via user_vessel_access — the SAME gate as GET /api/vessels).
 * No ids are sent from the client, so scope can never be widened. The caller
 * renders flags only for the ids in its already server-scoped VesselContext
 * list, so there is zero client-side widening.
 *
 * NO FACILITY PATH: this vessels-only slice derives attention for vessels only.
 */

import { extractItems } from '../sync/extractItems';
import { azureApiClient } from '../lib/azureApiClient';
import { summarizeJob } from '../services/equipmentMaintenanceService';
import { getJobFrequencyDays } from './intervalHelpers';

// Vessel compliance "expiring" window. Mirrors useComplianceRegister.calculateStatus
// (<= 30 days → 'expiring', < 0 → 'expired'). Kept as a named constant so the bell
// (P-03) and the card share ONE threshold.
export const VESSEL_CERT_WARNING_DAYS = 30;

// Vessel compliance categories that raise a "certificate/document expiring" flag.
// FT-441: sms_document ADDED — an expired policy/SMS document is a real overdue
// safety item the in-vessel Compliance Register surfaces; dropping it hid it.
// risk_assessment is deliberately excluded (its review cadence is a separate
// surface and the flag wording is certificate/document-oriented).
const VESSEL_EXPIRY_CATEGORIES = new Set(['certificate', 'sms_document']);

/**
 * Parse a date string as LOCAL time to avoid a ±1 day UTC-midnight shift.
 * Mirrors useNormalizedMaintenanceJobs.parseLocalDate so overdue day-boundaries
 * match the dashboard exactly.
 */
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Reproduce the in-vessel DASHBOARD's overdue status for ONE maintenance job.
 * Direct port of the derivation inside `useNormalizedMaintenanceJobs` — the
 * single source of truth the dashboard's overdue count comes from. Kept pure so
 * the fleet card and the dashboard can never disagree.
 *
 * Resolution order (matches the hook exactly):
 *   1. summarizeJob(...).nextDue  — from maintenance_history completions
 *   2. lastCompleted + interval    — getJobFrequencyDays(job)
 *   3. job.initialNextDue          — explicit due date at job creation
 *   4. due today                   — never completed, no initial due
 * Overdue ⇔ daysUntilDue < 0.
 */
export function deriveNormalizedMaintenanceStatus(job, equipmentMap, maintenanceHistory, advanceWarningDays = 30) {
  const summary = summarizeJob(job, equipmentMap, maintenanceHistory);
  const lastCompleted = summary.lastCompleted;

  const today = startOfToday();
  let daysUntilDue;

  if (summary.nextDue) {
    const nd = parseLocalDate(summary.nextDue);
    if (nd) {
      nd.setHours(0, 0, 0, 0);
      daysUntilDue = Math.ceil((nd - today) / (1000 * 60 * 60 * 24));
    } else {
      daysUntilDue = 0;
    }
  } else if (lastCompleted) {
    const lastDate = parseLocalDate(lastCompleted);
    const intervalDays = getJobFrequencyDays(job);
    const nd = lastDate ? new Date(lastDate) : new Date(today);
    nd.setDate(nd.getDate() + intervalDays);
    nd.setHours(0, 0, 0, 0);
    daysUntilDue = Math.ceil((nd - today) / (1000 * 60 * 60 * 24));
  } else if (job.initialNextDue) {
    const nd = parseLocalDate(job.initialNextDue);
    if (nd) {
      nd.setHours(0, 0, 0, 0);
      daysUntilDue = Math.ceil((nd - today) / (1000 * 60 * 60 * 24));
    } else {
      daysUntilDue = 0;
    }
  } else {
    daysUntilDue = 0; // never completed, no initial due → due today (not overdue)
  }

  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= advanceWarningDays) return 'due_soon';
  return 'compliant';
}

/**
 * Count overdue vessel maintenance jobs, reproducing the dashboard's count.
 * Uses the NORMALIZED rule (summarizeJob + interval + initialNextDue). Archived
 * jobs are excluded (the dashboard's rawJobs list is tombstone-filtered).
 */
export function countOverdueVesselMaintenance(jobs, maintenanceHistory = [], equipment = [], advanceWarningDays = 30) {
  if (!Array.isArray(jobs)) return 0;
  const equipmentMap = new Map((Array.isArray(equipment) ? equipment : []).map((eq) => [eq.id, eq]));
  let overdue = 0;
  jobs.forEach((job) => {
    if (!job) return;
    if (job.status === 'archived') return; // tombstone — not a live job
    if (deriveNormalizedMaintenanceStatus(job, equipmentMap, maintenanceHistory, advanceWarningDays) === 'overdue') {
      overdue += 1;
    }
  });
  return overdue;
}

/**
 * Count expiring/expired vessel CERTIFICATES + SMS DOCUMENTS from the compliance
 * register. Mirrors useComplianceRegister.calculateStatus: an item counts when
 * its expiryDate is already past OR within `warningDays`. Legacy items with no
 * `category` are treated as certificates. Archived items are skipped.
 */
export function countExpiringVesselCerts(items, warningDays = VESSEL_CERT_WARNING_DAYS) {
  if (!Array.isArray(items)) return 0;
  const now = new Date();
  let count = 0;
  items.forEach((item) => {
    if (!item) return;
    if (item.status === 'archived') return;
    if (item.category && !VESSEL_EXPIRY_CATEGORIES.has(item.category)) return;
    if (!item.expiryDate) return;
    const expiry = new Date(item.expiryDate);
    if (Number.isNaN(expiry.getTime())) return;
    const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysUntil <= warningDays) count += 1; // includes already-expired (negative)
  });
  return count;
}

/**
 * Derive attention for ONE vessel from already-loaded collections. Pure w.r.t.
 * its inputs; the network read is isolated in `fetchFleetAttention`.
 * @returns {{ overdueMaintenance: number, expiringCerts: number, hasAttention: boolean }}
 */
export function deriveVesselAttention(
  jobs,
  complianceItems,
  maintenanceHistory = [],
  equipment = [],
  warningDays = VESSEL_CERT_WARNING_DAYS,
) {
  const overdueMaintenance = countOverdueVesselMaintenance(jobs, maintenanceHistory, equipment, warningDays);
  const expiringCerts = countExpiringVesselCerts(complianceItems, warningDays);
  return {
    overdueMaintenance,
    expiringCerts,
    hasAttention: overdueMaintenance > 0 || expiringCerts > 0,
  };
}

// ── Data loading (ONE batched server pull, READ-ONLY) ────────────────────────

export const ALL_CLEAR = Object.freeze({ overdueMaintenance: 0, expiringCerts: 0, hasAttention: false });
export const UNKNOWN = Object.freeze({ overdueMaintenance: 0, expiringCerts: 0, hasAttention: false, unknown: true });

/**
 * Unwrap a batched-endpoint collection payload into a flat item array via the
 * canonical `extractItems` (the SAME unwrap the sync bridge uses; FT-331 guard).
 */
function itemsFrom(rows) {
  return extractItems(Array.isArray(rows) ? rows : []);
}

/**
 * Derive attention for ONE vessel from its batched-endpoint collections object.
 */
function deriveVesselFromCollections(collections) {
  const jobs = itemsFrom(collections?.maintenance_jobs);
  const history = itemsFrom(collections?.maintenance_history);
  const compliance = itemsFrom(collections?.compliance_register);
  const equipment = itemsFrom(collections?.equipment_register);
  return deriveVesselAttention(jobs, compliance, history, equipment);
}

/**
 * Build a per-vessel attention map from a batched endpoint response, for the
 * REQUESTED id set.
 *
 * The "never a false all-clear" safety property is enforced here with an
 * ABSENT-vs-EMPTY sentinel: an id the server RETURNED resolves to its derived
 * truth (empty collections = a legitimate all-clear, because the server pull is
 * authoritative); an id that was requested but is ABSENT from the response
 * resolves to `unknown` ("couldn't check"), NEVER a false all-clear.
 *
 * @param {{ vessels?: Array }} data endpoint response
 * @param {string[]} vesselIds requested vessel ids
 * @returns {{ vessels: Record<string,object> }}
 */
export function buildFleetAttentionMap(data, vesselIds = []) {
  const vesselById = new Map(
    (Array.isArray(data?.vessels) ? data.vessels : []).map((v) => [v.vesselId, v.collections]),
  );

  const vessels = {};
  vesselIds.filter(Boolean).forEach((id) => {
    if (vesselById.has(id)) {
      try {
        vessels[id] = deriveVesselFromCollections(vesselById.get(id));
      } catch {
        // A malformed payload for one vessel must not manufacture a false clear.
        vessels[id] = { ...UNKNOWN };
      }
    } else {
      // Requested but not returned by the server → couldn't check.
      vessels[id] = { ...UNKNOWN };
    }
  });

  return { vessels };
}

/**
 * Fetch + derive attention for a whole fleet in ONE batched server call.
 *
 * READ-ONLY: hits GET /api/fleet/attention-data (a plain GET). On ANY failure
 * (network / auth / non-2xx — INCLUDING a 404 while the endpoint is not yet
 * deployed) EVERY requested vessel resolves to `unknown`, so the cards show
 * "Checking…" and NEVER a false "all up to date".
 *
 * @param {string[]} vesselIds server-scoped assigned vessel ids to render
 * @returns {Promise<{ vessels: Record<string,object> }>}
 */
export async function fetchFleetAttention(vesselIds = []) {
  const vIds = (vesselIds || []).filter(Boolean);
  try {
    const data = await azureApiClient.fetchFleetAttentionData();
    return buildFleetAttentionMap(data, vIds);
  } catch {
    // Whole-call failure → every requested vessel is `unknown` (never a clear).
    const vessels = Object.fromEntries(vIds.map((id) => [id, { ...UNKNOWN }]));
    return { vessels };
  }
}
